import { useMemo } from "react";
import { useAppSelector } from "@store/index";
import { chartConfigSelector } from "@store/slices/chart-config-slice";
import { useGetDatasetQuestionVariablesByElementQuery } from "@store/api/dataset-api";
import {
  getStackedVariableLabel,
  getStackedValueLabel,
} from "@utils/stacked-bar-utils";

export interface StackedUniverseItem {
  id: string;
  label: string;
  // Response (machine) label variants used to reconcile chart-data ids.
  matchLabels: string[];
}

const uniqueTruthy = (values: Array<string | undefined | null>): string[] =>
  Array.from(new Set(values.filter((v): v is string => !!v && v !== "")));

/**
 * Loads the grid question's full variable and value sets (survey order
 * preserved) for the Stacks/Segments selectors. Stacked bar always needs
 * these regardless of any focus selection — there is no Group By to gate on.
 */
export function useStackedBarVariables() {
  const { datasetId, questionId } = useAppSelector(chartConfigSelector);

  const { data, isFetching } = useGetDatasetQuestionVariablesByElementQuery(
    { id: datasetId!, dataset_element_id: questionId! },
    { skip: !datasetId || !questionId },
  );

  const variables = useMemo<StackedUniverseItem[]>(
    () =>
      (data?.variables ?? []).map((v) => ({
        id: v.datasetElementId,
        label: getStackedVariableLabel(v),
        matchLabels: uniqueTruthy([
          v.label,
          v.rowtitle,
          v.coltitle,
          v.title,
          v.value,
        ]),
      })),
    [data],
  );

  const values = useMemo<StackedUniverseItem[]>(
    () =>
      (data?.values ?? []).map((v) => ({
        id: v.datasetElementId,
        label: getStackedValueLabel(v),
        matchLabels: uniqueTruthy([
          v.label,
          v.title,
          v.rowtitle,
          v.coltitle,
          v.value != null ? String(v.value) : undefined,
        ]),
      })),
    [data],
  );

  return { variables, values, isFetching };
}
